import React, { useEffect, useRef } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ChatArea = ({ conversation, messages, isTyping, onBack, loading }) => {
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatDateHeader = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday?.setDate(yesterday?.getDate() - 1);

    if (date?.toDateString() === today?.toDateString()) return 'Today';
    if (date?.toDateString() === yesterday?.toDateString()) return 'Yesterday';
    return date?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <Icon name="Check" size={14} className="text-muted-foreground" strokeWidth={2} />;
      case 'delivered':
        return <Icon name="CheckCheck" size={14} className="text-muted-foreground" strokeWidth={2} />;
      case 'read':
        return <Icon name="CheckCheck" size={14} className="text-primary" strokeWidth={2} />;
      default:
        return null;
    }
  };

  const groupMessagesByDate = () => {
    const grouped = {};
    messages?.forEach(message => {
      const dateKey = formatDateHeader(message?.timestamp);
      if (!grouped?.[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped?.[dateKey]?.push(message);
    });
    return grouped;
  };

  const groupedMessages = groupMessagesByDate();

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Chat Header */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center space-x-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="lg:hidden"
          >
            <Icon name="ArrowLeft" size={20} strokeWidth={2} />
          </Button>

          <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-primary flex items-center justify-center">
            {conversation?.participantPhoto ? (
              <Image
                src={conversation?.participantPhoto}
                alt={`${conversation?.participantName} profile photo`}
                className="w-full h-full object-cover"
              />
            ) : (
              <Icon name="User" size={20} color="white" strokeWidth={2} />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-body font-semibold text-foreground">
              {conversation?.participantName}
            </h3>
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 rounded overflow-hidden">
                <Image
                  src={conversation?.trailerImage}
                  alt={conversation?.trailerImageAlt}
                  className="w-full h-full object-cover"
                />
              </div>
              <p className="font-caption text-xs text-muted-foreground truncate">
                {conversation?.trailerName} • {conversation?.rentalDates}
              </p>
            </div>
          </div>

          <Button 
            variant="outline" 
            size="sm"
            iconName="AlertTriangle"
            onClick={() => window.location.href = `/dispute-resolution?bookingId=${conversation?.bookingId}&conversationId=${conversation?.id}`}
            className="hidden sm:flex"
          >
            File Dispute
          </Button>

          <Button variant="ghost" size="icon">
            <Icon name="MoreVertical" size={20} strokeWidth={2} />
          </Button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {Object?.entries(groupedMessages)?.map(([date, dateMessages]) => (
          <div key={date}>
            {/* Date Header */}
            <div className="flex items-center justify-center mb-4">
              <span className="px-3 py-1 bg-muted rounded-full text-xs font-medium text-muted-foreground">
                {date}
              </span>
            </div>

            {/* Messages */}
            {dateMessages?.map((message) => {
              const isMe = message?.senderId === 'me';
              return (
                <div
                  key={message?.id}
                  className={`flex mb-3 ${isMe ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[70%] ${isMe ? 'order-2' : 'order-1'}`}>
                    <div
                      className={`rounded-lg px-4 py-2 ${
                        isMe
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-foreground'
                      }`}
                    >
                      <p className="font-body text-sm">{message?.content}</p>
                    </div>
                    <div className={`flex items-center space-x-1 mt-1 ${
                      isMe ? 'justify-end' : 'justify-start'
                    }`}>
                      <span className="font-caption text-xs text-muted-foreground">
                        {formatTimestamp(message?.timestamp)}
                      </span>
                      {isMe && getStatusIcon(message?.status)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        {/* Typing Indicator */}
        {isTyping && (
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
              <Icon name="User" size={16} color="white" strokeWidth={2} />
            </div>
            <div className="bg-muted rounded-lg px-4 py-2">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default ChatArea;